/**
 * FIKO ADVANCED EDGE DETECTION SYSTEM
 * ====================================
 * FAZA 4.3: CATIA seviyesi edge detection ve rendering
 *
 * CAD uygulamalari icin profesyonel edge detection
 *
 * Ozellikler:
 * - BVH-accelerated edge finding
 * - Clip plane intersection edges (real-time)
 * - Screen-space edge detection (depth + normal discontinuities)
 * - Silhouette edge detection
 * - Crease/sharp edge detection
 * - Multi-pass edge rendering
 * - GPU-accelerated edge computation
 *
 * HEDEFLER:
 * - <1ms edge detection per object
 * - Real-time clip plane edges
 * - CAD-quality edge rendering
 * - Dynamic edge width based on importance
 */

import * as THREE from 'three';

// ============================================================================
// INTERFACES & TYPES
// ============================================================================

export interface EdgeDetectionConfig {
    /** Enable silhouette edge detection */
    detectSilhouettes: boolean;
    /** Enable crease/sharp edge detection */
    detectCreases: boolean;
    /** Crease angle threshold in degrees */
    creaseAngleThreshold: number;
    /** Enable screen-space edge detection */
    useScreenSpace: boolean;
    /** Edge width in pixels */
    edgeWidth: number;
    /** Edge color */
    edgeColor: THREE.Color;
    /** Enable anti-aliased edges */
    antialiased: boolean;
    /** Edge fade distance (world units) */
    fadeDistance: number;
    /** Maximum edges per frame */
    maxEdgesPerFrame: number;
}

export interface DetectedEdge {
    /** Start point in world space */
    start: THREE.Vector3;
    /** End point in world space */
    end: THREE.Vector3;
    /** Edge type */
    type: 'silhouette' | 'crease' | 'boundary' | 'clip' | 'feature';
    /** Importance/weight (0-1) */
    importance: number;
    /** Face indices sharing this edge */
    faceIndices: [number, number] | [number];
    /** Associated mesh ID */
    meshId: string;
}

export interface ClipPlane {
    /** Plane equation */
    plane: THREE.Plane;
    /** Is plane active */
    active: boolean;
    /** Clip edge color */
    edgeColor: THREE.Color;
    /** Clip edge width */
    edgeWidth: number;
}

export interface EdgeRenderTarget {
    /** Render target for edge texture */
    target: THREE.WebGLRenderTarget;
    /** Material for edge rendering */
    material: THREE.ShaderMaterial;
}

export interface EdgeDetectionStats {
    totalEdges: number;
    silhouetteEdges: number;
    creaseEdges: number;
    clipEdges: number;
    detectionTimeMs: number;
    renderTimeMs: number;
    meshesProcessed: number;
}

// ============================================================================
// EDGE VERTEX SHADER
// ============================================================================

const EDGE_VERTEX_SHADER = `
attribute vec3 instanceStart;
attribute vec3 instanceEnd;
attribute vec4 instanceColor;
attribute float instanceWidth;

uniform mat4 modelViewMatrix;
uniform mat4 projectionMatrix;
uniform vec2 resolution;

varying vec4 vColor;
varying vec2 vUV;

void main() {
    vColor = instanceColor;

    // Transform endpoints to clip space
    vec4 clipStart = projectionMatrix * modelViewMatrix * vec4(instanceStart, 1.0);
    vec4 clipEnd = projectionMatrix * modelViewMatrix * vec4(instanceEnd, 1.0);

    // Perspective divide
    vec2 ndcStart = clipStart.xy / clipStart.w;
    vec2 ndcEnd = clipEnd.xy / clipEnd.w;

    // Calculate line direction and perpendicular
    vec2 lineDir = normalize(ndcEnd - ndcStart);
    vec2 perpDir = vec2(-lineDir.y, lineDir.x);

    // Calculate screen-space offset
    float aspect = resolution.x / resolution.y;
    vec2 offset = perpDir * instanceWidth / resolution;
    offset.x /= aspect;

    // Determine which vertex of the quad we're at
    float side = mod(float(gl_VertexID), 2.0) * 2.0 - 1.0;
    float end = floor(float(gl_VertexID) / 2.0);

    // Interpolate between start and end
    vec4 clipPos = mix(clipStart, clipEnd, end);
    clipPos.xy += offset * side * clipPos.w;

    gl_Position = clipPos;

    vUV = vec2(end, side * 0.5 + 0.5);
}
`;

// ============================================================================
// EDGE FRAGMENT SHADER
// ============================================================================

const EDGE_FRAGMENT_SHADER = `
precision highp float;

varying vec4 vColor;
varying vec2 vUV;

uniform float opacity;
uniform bool antialiased;

void main() {
    vec4 color = vColor;

    if (antialiased) {
        // Smooth edges at line endpoints and sides
        float edgeDist = abs(vUV.y - 0.5) * 2.0;
        float alpha = 1.0 - smoothstep(0.7, 1.0, edgeDist);
        color.a *= alpha;
    }

    color.a *= opacity;

    if (color.a < 0.01) discard;

    gl_FragColor = color;
}
`;

// ============================================================================
// SCREEN-SPACE EDGE DETECTION SHADERS
// ============================================================================

const SCREEN_SPACE_EDGE_VERTEX = `
varying vec2 vUV;
void main() {
    vUV = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const SCREEN_SPACE_EDGE_FRAGMENT = `
precision highp float;

uniform sampler2D tDepth;
uniform sampler2D tNormal;
uniform vec2 resolution;
uniform float depthThreshold;
uniform float normalThreshold;
uniform vec3 edgeColor;

varying vec2 vUV;

float getDepth(vec2 uv) {
    return texture2D(tDepth, uv).r;
}

vec3 getNormal(vec2 uv) {
    return texture2D(tNormal, uv).xyz * 2.0 - 1.0;
}

void main() {
    vec2 texelSize = 1.0 / resolution;

    // Sample depth at neighboring pixels
    float depthC = getDepth(vUV);
    float depthL = getDepth(vUV - vec2(texelSize.x, 0.0));
    float depthR = getDepth(vUV + vec2(texelSize.x, 0.0));
    float depthT = getDepth(vUV - vec2(0.0, texelSize.y));
    float depthB = getDepth(vUV + vec2(0.0, texelSize.y));

    // Depth discontinuity
    float depthEdge = abs(depthL - depthR) + abs(depthT - depthB);
    depthEdge = step(depthThreshold, depthEdge);

    // Sample normals at neighboring pixels
    vec3 normalC = getNormal(vUV);
    vec3 normalL = getNormal(vUV - vec2(texelSize.x, 0.0));
    vec3 normalR = getNormal(vUV + vec2(texelSize.x, 0.0));
    vec3 normalT = getNormal(vUV - vec2(0.0, texelSize.y));
    vec3 normalB = getNormal(vUV + vec2(0.0, texelSize.y));

    // Normal discontinuity
    float normalEdge = 0.0;
    normalEdge += 1.0 - dot(normalC, normalL);
    normalEdge += 1.0 - dot(normalC, normalR);
    normalEdge += 1.0 - dot(normalC, normalT);
    normalEdge += 1.0 - dot(normalC, normalB);
    normalEdge = step(normalThreshold, normalEdge);

    // Combine edges
    float edge = max(depthEdge, normalEdge);

    gl_FragColor = vec4(edgeColor, edge);
}
`;

// ============================================================================
// HELPER CLASSES
// ============================================================================

/**
 * Edge data structure for efficient storage
 */
class EdgeBuffer {
    private starts: Float32Array;
    private ends: Float32Array;
    private colors: Float32Array;
    private widths: Float32Array;
    private count: number = 0;
    private capacity: number;

    constructor(capacity: number = 10000) {
        this.capacity = capacity;
        this.starts = new Float32Array(capacity * 3);
        this.ends = new Float32Array(capacity * 3);
        this.colors = new Float32Array(capacity * 4);
        this.widths = new Float32Array(capacity);
    }

    addEdge(
        start: THREE.Vector3,
        end: THREE.Vector3,
        color: THREE.Color,
        width: number,
        alpha: number = 1
    ): boolean {
        if (this.count >= this.capacity) {
            return false;
        }

        const idx = this.count * 3;
        const colorIdx = this.count * 4;

        this.starts[idx] = start.x;
        this.starts[idx + 1] = start.y;
        this.starts[idx + 2] = start.z;

        this.ends[idx] = end.x;
        this.ends[idx + 1] = end.y;
        this.ends[idx + 2] = end.z;

        this.colors[colorIdx] = color.r;
        this.colors[colorIdx + 1] = color.g;
        this.colors[colorIdx + 2] = color.b;
        this.colors[colorIdx + 3] = alpha;

        this.widths[this.count] = width;

        this.count++;
        return true;
    }

    clear(): void {
        this.count = 0;
    }

    getCount(): number {
        return this.count;
    }

    getStarts(): Float32Array {
        return this.starts.subarray(0, this.count * 3);
    }

    getEnds(): Float32Array {
        return this.ends.subarray(0, this.count * 3);
    }

    getColors(): Float32Array {
        return this.colors.subarray(0, this.count * 4);
    }

    getWidths(): Float32Array {
        return this.widths.subarray(0, this.count);
    }

    resize(newCapacity: number): void {
        if (newCapacity <= this.capacity) return;

        const newStarts = new Float32Array(newCapacity * 3);
        const newEnds = new Float32Array(newCapacity * 3);
        const newColors = new Float32Array(newCapacity * 4);
        const newWidths = new Float32Array(newCapacity);

        newStarts.set(this.starts.subarray(0, this.count * 3));
        newEnds.set(this.ends.subarray(0, this.count * 3));
        newColors.set(this.colors.subarray(0, this.count * 4));
        newWidths.set(this.widths.subarray(0, this.count));

        this.starts = newStarts;
        this.ends = newEnds;
        this.colors = newColors;
        this.widths = newWidths;
        this.capacity = newCapacity;
    }
}

/**
 * Edge hash table for deduplication
 */
class EdgeHashTable {
    private map: Map<string, DetectedEdge> = new Map();

    private hashEdge(start: THREE.Vector3, end: THREE.Vector3): string {
        // Order-independent hash
        const a = `${start.x.toFixed(4)},${start.y.toFixed(4)},${start.z.toFixed(4)}`;
        const b = `${end.x.toFixed(4)},${end.y.toFixed(4)},${end.z.toFixed(4)}`;
        return a < b ? `${a}|${b}` : `${b}|${a}`;
    }

    add(edge: DetectedEdge): boolean {
        const hash = this.hashEdge(edge.start, edge.end);
        if (this.map.has(hash)) {
            // Update importance if higher
            const existing = this.map.get(hash)!;
            if (edge.importance > existing.importance) {
                existing.importance = edge.importance;
            }
            return false;
        }
        this.map.set(hash, edge);
        return true;
    }

    has(start: THREE.Vector3, end: THREE.Vector3): boolean {
        return this.map.has(this.hashEdge(start, end));
    }

    clear(): void {
        this.map.clear();
    }

    getAll(): DetectedEdge[] {
        return Array.from(this.map.values());
    }

    get size(): number {
        return this.map.size;
    }
}

// ============================================================================
// MAIN CLASS
// ============================================================================

export class FikoAdvancedEdgeDetection {
    private config: EdgeDetectionConfig;
    private camera: THREE.Camera;
    private renderer: THREE.WebGLRenderer;
    private scene: THREE.Scene;

    // Edge storage
    private edgeBuffer: EdgeBuffer;
    private edgeHashTable: EdgeHashTable = new EdgeHashTable();
    private detectedEdges: DetectedEdge[] = [];

    // Clip planes
    private clipPlanes: Map<string, ClipPlane> = new Map();

    // Edge mesh for rendering
    private edgeMesh: THREE.Mesh | null = null;
    private edgeGeometry: THREE.InstancedBufferGeometry | null = null;
    private edgeMaterial: THREE.ShaderMaterial | null = null;

    // Screen-space edge detection
    private depthTarget: THREE.WebGLRenderTarget | null = null;
    private normalTarget: THREE.WebGLRenderTarget | null = null;
    private screenSpaceMaterial: THREE.ShaderMaterial | null = null;
    private screenSpaceQuad: THREE.Mesh | null = null;

    // Registered meshes
    private registeredMeshes: Map<string, {
        mesh: THREE.Mesh;
        edges: DetectedEdge[];
        lastUpdate: number;
    }> = new Map();

    // Pre-allocated
    private static readonly _tempVec3A = new THREE.Vector3();
    private static readonly _tempVec3B = new THREE.Vector3();
    private static readonly _tempVec3C = new THREE.Vector3();
    private static readonly _tempVec3D = new THREE.Vector3();
    private static readonly _tempPlane = new THREE.Plane();
    private static readonly _tempLine3 = new THREE.Line3();
    private static readonly _tempMatrix = new THREE.Matrix4();

    // Stats
    private stats: EdgeDetectionStats = {
        totalEdges: 0,
        silhouetteEdges: 0,
        creaseEdges: 0,
        clipEdges: 0,
        detectionTimeMs: 0,
        renderTimeMs: 0,
        meshesProcessed: 0
    };

    constructor(
        camera: THREE.Camera,
        renderer: THREE.WebGLRenderer,
        scene: THREE.Scene,
        config?: Partial<EdgeDetectionConfig>
    ) {
        this.camera = camera;
        this.renderer = renderer;
        this.scene = scene;

        this.config = {
            detectSilhouettes: config?.detectSilhouettes ?? true,
            detectCreases: config?.detectCreases ?? true,
            creaseAngleThreshold: config?.creaseAngleThreshold ?? 30,
            useScreenSpace: config?.useScreenSpace ?? false,
            edgeWidth: config?.edgeWidth ?? 1.5,
            edgeColor: config?.edgeColor ?? new THREE.Color(0x000000),
            antialiased: config?.antialiased ?? true,
            fadeDistance: config?.fadeDistance ?? 100,
            maxEdgesPerFrame: config?.maxEdgesPerFrame ?? 50000
        };

        this.edgeBuffer = new EdgeBuffer(this.config.maxEdgesPerFrame);

        this.initEdgeRendering();

        if (this.config.useScreenSpace) {
            this.initScreenSpaceDetection();
        }
    }

    // ========================================================================
    // INITIALIZATION
    // ========================================================================

    /**
     * Initialize edge rendering system
     */
    private initEdgeRendering(): void {
        // Create instanced geometry for line segments
        this.edgeGeometry = new THREE.InstancedBufferGeometry();

        // Base quad vertices (4 vertices per line segment)
        const quadPositions = new Float32Array([
            0, -0.5, 0,
            0, 0.5, 0,
            1, -0.5, 0,
            1, 0.5, 0
        ]);
        const quadIndices = new Uint16Array([0, 1, 2, 1, 3, 2]);

        this.edgeGeometry.setAttribute('position', new THREE.BufferAttribute(quadPositions, 3));
        this.edgeGeometry.setIndex(new THREE.BufferAttribute(quadIndices, 1));

        // Instance attributes (will be updated each frame)
        this.edgeGeometry.setAttribute('instanceStart',
            new THREE.InstancedBufferAttribute(new Float32Array(3), 3));
        this.edgeGeometry.setAttribute('instanceEnd',
            new THREE.InstancedBufferAttribute(new Float32Array(3), 3));
        this.edgeGeometry.setAttribute('instanceColor',
            new THREE.InstancedBufferAttribute(new Float32Array(4), 4));
        this.edgeGeometry.setAttribute('instanceWidth',
            new THREE.InstancedBufferAttribute(new Float32Array(1), 1));

        // Edge material
        this.edgeMaterial = new THREE.ShaderMaterial({
            vertexShader: EDGE_VERTEX_SHADER,
            fragmentShader: EDGE_FRAGMENT_SHADER,
            uniforms: {
                resolution: { value: new THREE.Vector2() },
                opacity: { value: 1.0 },
                antialiased: { value: this.config.antialiased }
            },
            transparent: true,
            depthTest: true,
            depthWrite: false,
            blending: THREE.NormalBlending
        });

        this.edgeMesh = new THREE.Mesh(this.edgeGeometry, this.edgeMaterial);
        this.edgeMesh.frustumCulled = false;
        this.edgeMesh.renderOrder = 1000; // Render after main scene
    }

    /**
     * Initialize screen-space edge detection
     */
    private initScreenSpaceDetection(): void {
        const size = this.renderer.getSize(new THREE.Vector2());

        // Depth render target
        this.depthTarget = new THREE.WebGLRenderTarget(size.x, size.y, {
            minFilter: THREE.NearestFilter,
            magFilter: THREE.NearestFilter,
            format: THREE.RGBAFormat,
            type: THREE.FloatType
        });

        // Normal render target
        this.normalTarget = new THREE.WebGLRenderTarget(size.x, size.y, {
            minFilter: THREE.NearestFilter,
            magFilter: THREE.NearestFilter,
            format: THREE.RGBAFormat,
            type: THREE.UnsignedByteType
        });

        // Screen-space edge material
        this.screenSpaceMaterial = new THREE.ShaderMaterial({
            vertexShader: SCREEN_SPACE_EDGE_VERTEX,
            fragmentShader: SCREEN_SPACE_EDGE_FRAGMENT,
            uniforms: {
                tDepth: { value: null },
                tNormal: { value: null },
                resolution: { value: size },
                depthThreshold: { value: 0.01 },
                normalThreshold: { value: 0.5 },
                edgeColor: { value: this.config.edgeColor }
            },
            transparent: true
        });

        // Full-screen quad
        const quadGeom = new THREE.PlaneGeometry(2, 2);
        this.screenSpaceQuad = new THREE.Mesh(quadGeom, this.screenSpaceMaterial);
    }

    // ========================================================================
    // MESH REGISTRATION
    // ========================================================================

    /**
     * Register a mesh for edge detection
     */
    registerMesh(id: string, mesh: THREE.Mesh): void {
        if (this.registeredMeshes.has(id)) return;

        this.registeredMeshes.set(id, {
            mesh,
            edges: [],
            lastUpdate: 0
        });
    }

    /**
     * Unregister a mesh
     */
    unregisterMesh(id: string): void {
        this.registeredMeshes.delete(id);
    }

    // ========================================================================
    // CLIP PLANE MANAGEMENT
    // ========================================================================

    /**
     * Add a clip plane for edge detection
     */
    addClipPlane(id: string, plane: THREE.Plane, color?: THREE.Color, width?: number): void {
        this.clipPlanes.set(id, {
            plane: plane.clone(),
            active: true,
            edgeColor: color || new THREE.Color(0xff0000),
            edgeWidth: width || 2
        });
    }

    /**
     * Update clip plane
     */
    updateClipPlane(id: string, plane: THREE.Plane): void {
        const clipPlane = this.clipPlanes.get(id);
        if (clipPlane) {
            clipPlane.plane.copy(plane);
        }
    }

    /**
     * Set clip plane active state
     */
    setClipPlaneActive(id: string, active: boolean): void {
        const clipPlane = this.clipPlanes.get(id);
        if (clipPlane) {
            clipPlane.active = active;
        }
    }

    /**
     * Remove clip plane
     */
    removeClipPlane(id: string): void {
        this.clipPlanes.delete(id);
    }

    // ========================================================================
    // EDGE DETECTION
    // ========================================================================

    /**
     * Update edge detection for all registered meshes
     */
    update(): void {
        const startTime = performance.now();

        this.edgeBuffer.clear();
        this.edgeHashTable.clear();
        this.detectedEdges = [];
        this.stats.silhouetteEdges = 0;
        this.stats.creaseEdges = 0;
        this.stats.clipEdges = 0;
        this.stats.meshesProcessed = 0;

        // Get camera position for silhouette detection
        const cameraPos = this.camera.position;

        // Process each registered mesh
        for (const [id, entry] of this.registeredMeshes) {
            if (!entry.mesh.visible) continue;

            this.processMesh(id, entry.mesh, cameraPos);
            this.stats.meshesProcessed++;
        }

        // Process clip plane intersections
        if (this.clipPlanes.size > 0) {
            this.processClipPlaneEdges();
        }

        // Update stats
        this.stats.totalEdges = this.edgeBuffer.getCount();
        this.stats.detectionTimeMs = performance.now() - startTime;

        // Update edge mesh
        this.updateEdgeMesh();
    }

    /**
     * Process single mesh for edges
     */
    private processMesh(meshId: string, mesh: THREE.Mesh, cameraPos: THREE.Vector3): void {
        const geometry = mesh.geometry as THREE.BufferGeometry;
        if (!geometry) return;

        const position = geometry.getAttribute('position');
        const index = geometry.getIndex();
        if (!position) return;

        // Get world matrix
        const worldMatrix = mesh.matrixWorld;

        // Build edge adjacency if needed
        const edgeData = this.buildEdgeAdjacency(geometry);

        // Process edges
        const creaseThreshold = Math.cos((this.config.creaseAngleThreshold * Math.PI) / 180);

        for (const [edgeKey, edgeInfo] of edgeData) {
            const [i1, i2] = edgeKey.split('-').map(Number);

            // Get world-space vertices
            FikoAdvancedEdgeDetection._tempVec3A.set(
                position.getX(i1), position.getY(i1), position.getZ(i1)
            ).applyMatrix4(worldMatrix);

            FikoAdvancedEdgeDetection._tempVec3B.set(
                position.getX(i2), position.getY(i2), position.getZ(i2)
            ).applyMatrix4(worldMatrix);

            let isEdge = false;
            let edgeType: DetectedEdge['type'] = 'feature';
            let importance = 0;

            // Boundary edge (only one adjacent face)
            if (edgeInfo.faceCount === 1) {
                isEdge = true;
                edgeType = 'boundary';
                importance = 1;
            }
            // Two adjacent faces - check for silhouette and crease
            else if (edgeInfo.faceCount === 2) {
                // Silhouette edge detection
                if (this.config.detectSilhouettes) {
                    const facing1 = this.isFaceFrontFacing(edgeInfo.normals[0], cameraPos,
                        FikoAdvancedEdgeDetection._tempVec3A);
                    const facing2 = this.isFaceFrontFacing(edgeInfo.normals[1], cameraPos,
                        FikoAdvancedEdgeDetection._tempVec3A);

                    if (facing1 !== facing2) {
                        isEdge = true;
                        edgeType = 'silhouette';
                        importance = 0.9;
                        this.stats.silhouetteEdges++;
                    }
                }

                // Crease edge detection
                if (!isEdge && this.config.detectCreases) {
                    const dot = edgeInfo.normals[0].dot(edgeInfo.normals[1]);
                    if (dot < creaseThreshold) {
                        isEdge = true;
                        edgeType = 'crease';
                        importance = 1 - dot;
                        this.stats.creaseEdges++;
                    }
                }
            }

            if (isEdge) {
                this.addDetectedEdge(
                    FikoAdvancedEdgeDetection._tempVec3A.clone(),
                    FikoAdvancedEdgeDetection._tempVec3B.clone(),
                    edgeType,
                    importance,
                    meshId
                );
            }
        }
    }

    /**
     * Build edge adjacency map for geometry
     */
    private buildEdgeAdjacency(geometry: THREE.BufferGeometry): Map<string, {
        faceCount: number;
        normals: THREE.Vector3[];
    }> {
        const edgeMap = new Map<string, { faceCount: number; normals: THREE.Vector3[] }>();
        const position = geometry.getAttribute('position');
        const index = geometry.getIndex();

        if (!index) return edgeMap;

        // Process each triangle
        for (let i = 0; i < index.count; i += 3) {
            const a = index.getX(i);
            const b = index.getX(i + 1);
            const c = index.getX(i + 2);

            // Calculate face normal
            FikoAdvancedEdgeDetection._tempVec3A.set(
                position.getX(a), position.getY(a), position.getZ(a)
            );
            FikoAdvancedEdgeDetection._tempVec3B.set(
                position.getX(b), position.getY(b), position.getZ(b)
            );
            FikoAdvancedEdgeDetection._tempVec3C.set(
                position.getX(c), position.getY(c), position.getZ(c)
            );

            FikoAdvancedEdgeDetection._tempVec3D.subVectors(
                FikoAdvancedEdgeDetection._tempVec3B,
                FikoAdvancedEdgeDetection._tempVec3A
            );
            const edge2 = new THREE.Vector3().subVectors(
                FikoAdvancedEdgeDetection._tempVec3C,
                FikoAdvancedEdgeDetection._tempVec3A
            );
            const normal = FikoAdvancedEdgeDetection._tempVec3D.clone().cross(edge2).normalize();

            // Add edges to map
            const edges = [
                [Math.min(a, b), Math.max(a, b)],
                [Math.min(b, c), Math.max(b, c)],
                [Math.min(c, a), Math.max(c, a)]
            ];

            for (const [e1, e2] of edges) {
                const key = `${e1}-${e2}`;
                if (!edgeMap.has(key)) {
                    edgeMap.set(key, { faceCount: 0, normals: [] });
                }
                const entry = edgeMap.get(key)!;
                entry.faceCount++;
                entry.normals.push(normal.clone());
            }
        }

        return edgeMap;
    }

    /**
     * Check if face is front-facing relative to camera
     */
    private isFaceFrontFacing(
        normal: THREE.Vector3,
        cameraPos: THREE.Vector3,
        facePoint: THREE.Vector3
    ): boolean {
        FikoAdvancedEdgeDetection._tempVec3D.subVectors(cameraPos, facePoint);
        return normal.dot(FikoAdvancedEdgeDetection._tempVec3D) > 0;
    }

    /**
     * Process clip plane intersection edges
     */
    private processClipPlaneEdges(): void {
        for (const [planeId, clipPlane] of this.clipPlanes) {
            if (!clipPlane.active) continue;

            for (const [meshId, entry] of this.registeredMeshes) {
                if (!entry.mesh.visible) continue;

                this.findClipPlaneEdges(meshId, entry.mesh, clipPlane);
            }
        }
    }

    /**
     * Find edges where mesh intersects clip plane
     */
    private findClipPlaneEdges(meshId: string, mesh: THREE.Mesh, clipPlane: ClipPlane): void {
        const geometry = mesh.geometry as THREE.BufferGeometry;
        if (!geometry) return;

        const position = geometry.getAttribute('position');
        const index = geometry.getIndex();
        if (!position || !index) return;

        const worldMatrix = mesh.matrixWorld;
        const plane = clipPlane.plane;

        // Process each triangle
        for (let i = 0; i < index.count; i += 3) {
            const indices = [index.getX(i), index.getX(i + 1), index.getX(i + 2)];
            const vertices: THREE.Vector3[] = [];
            const signs: number[] = [];

            // Get world-space vertices and their plane distances
            for (const idx of indices) {
                const v = new THREE.Vector3(
                    position.getX(idx),
                    position.getY(idx),
                    position.getZ(idx)
                ).applyMatrix4(worldMatrix);
                vertices.push(v);
                signs.push(Math.sign(plane.distanceToPoint(v)));
            }

            // Find intersection points
            const intersections: THREE.Vector3[] = [];

            for (let j = 0; j < 3; j++) {
                const k = (j + 1) % 3;
                if (signs[j] !== signs[k] && signs[j] !== 0 && signs[k] !== 0) {
                    // Edge crosses plane
                    const line = new THREE.Line3(vertices[j], vertices[k]);
                    const intersection = new THREE.Vector3();
                    if (plane.intersectLine(line, intersection)) {
                        intersections.push(intersection);
                    }
                } else if (signs[j] === 0) {
                    intersections.push(vertices[j].clone());
                }
            }

            // If we have exactly 2 intersections, we have an edge
            if (intersections.length === 2) {
                this.addDetectedEdge(
                    intersections[0],
                    intersections[1],
                    'clip',
                    1,
                    meshId,
                    clipPlane.edgeColor,
                    clipPlane.edgeWidth
                );
                this.stats.clipEdges++;
            }
        }
    }

    /**
     * Add detected edge
     */
    private addDetectedEdge(
        start: THREE.Vector3,
        end: THREE.Vector3,
        type: DetectedEdge['type'],
        importance: number,
        meshId: string,
        color?: THREE.Color,
        width?: number
    ): void {
        // Skip degenerate edges
        if (start.distanceToSquared(end) < 0.0001) return;

        // Check buffer capacity
        if (this.edgeBuffer.getCount() >= this.config.maxEdgesPerFrame) return;

        // Add to buffer
        const edgeColor = color || this.config.edgeColor;
        const edgeWidth = width || this.config.edgeWidth;

        this.edgeBuffer.addEdge(start, end, edgeColor, edgeWidth, importance);

        // Store detected edge info
        this.detectedEdges.push({
            start: start.clone(),
            end: end.clone(),
            type,
            importance,
            faceIndices: [-1],
            meshId
        });
    }

    // ========================================================================
    // RENDERING
    // ========================================================================

    /**
     * Update edge mesh with current edge data
     */
    private updateEdgeMesh(): void {
        if (!this.edgeGeometry || !this.edgeMaterial) return;

        const count = this.edgeBuffer.getCount();
        if (count === 0) {
            this.edgeGeometry.instanceCount = 0;
            return;
        }

        // Update instance attributes
        const starts = this.edgeBuffer.getStarts();
        const ends = this.edgeBuffer.getEnds();
        const colors = this.edgeBuffer.getColors();
        const widths = this.edgeBuffer.getWidths();

        this.edgeGeometry.setAttribute('instanceStart',
            new THREE.InstancedBufferAttribute(starts, 3));
        this.edgeGeometry.setAttribute('instanceEnd',
            new THREE.InstancedBufferAttribute(ends, 3));
        this.edgeGeometry.setAttribute('instanceColor',
            new THREE.InstancedBufferAttribute(colors, 4));
        this.edgeGeometry.setAttribute('instanceWidth',
            new THREE.InstancedBufferAttribute(widths, 1));

        this.edgeGeometry.instanceCount = count;

        // Update resolution uniform
        const size = this.renderer.getSize(new THREE.Vector2());
        this.edgeMaterial.uniforms.resolution.value.copy(size);
    }

    /**
     * Render edges
     */
    render(): void {
        const startTime = performance.now();

        if (!this.edgeMesh || this.edgeBuffer.getCount() === 0) {
            this.stats.renderTimeMs = 0;
            return;
        }

        // Add edge mesh to scene if not already added
        if (!this.edgeMesh.parent) {
            this.scene.add(this.edgeMesh);
        }

        // Screen-space edge detection (optional)
        if (this.config.useScreenSpace && this.screenSpaceQuad) {
            this.renderScreenSpaceEdges();
        }

        this.stats.renderTimeMs = performance.now() - startTime;
    }

    /**
     * Render screen-space edge detection
     */
    private renderScreenSpaceEdges(): void {
        if (!this.depthTarget || !this.normalTarget || !this.screenSpaceMaterial) return;

        // Would need to render depth and normal passes first
        // Then apply screen-space edge detection

        // Update uniforms
        this.screenSpaceMaterial.uniforms.tDepth.value = this.depthTarget.texture;
        this.screenSpaceMaterial.uniforms.tNormal.value = this.normalTarget.texture;
    }

    /**
     * Get edge mesh for manual scene management
     */
    getEdgeMesh(): THREE.Mesh | null {
        return this.edgeMesh;
    }

    // ========================================================================
    // PUBLIC API
    // ========================================================================

    /**
     * Get detected edges
     */
    getDetectedEdges(): DetectedEdge[] {
        return [...this.detectedEdges];
    }

    /**
     * Get statistics
     */
    getStats(): EdgeDetectionStats {
        return { ...this.stats };
    }

    /**
     * Set edge configuration
     */
    setConfig(config: Partial<EdgeDetectionConfig>): void {
        Object.assign(this.config, config);

        if (this.edgeMaterial) {
            this.edgeMaterial.uniforms.antialiased.value = this.config.antialiased;
        }
    }

    /**
     * Dispose all resources
     */
    dispose(): void {
        if (this.edgeMesh?.parent) {
            this.edgeMesh.parent.remove(this.edgeMesh);
        }

        this.edgeGeometry?.dispose();
        this.edgeMaterial?.dispose();
        this.depthTarget?.dispose();
        this.normalTarget?.dispose();
        this.screenSpaceMaterial?.dispose();

        this.registeredMeshes.clear();
        this.clipPlanes.clear();
    }
}

// ============================================================================
// SINGLETON & FACTORY
// ============================================================================

let _edgeDetectionInstance: FikoAdvancedEdgeDetection | null = null;

/**
 * Get or create edge detection instance
 */
export function getAdvancedEdgeDetection(
    camera?: THREE.Camera,
    renderer?: THREE.WebGLRenderer,
    scene?: THREE.Scene,
    config?: Partial<EdgeDetectionConfig>
): FikoAdvancedEdgeDetection | null {
    if (!_edgeDetectionInstance && camera && renderer && scene) {
        _edgeDetectionInstance = new FikoAdvancedEdgeDetection(camera, renderer, scene, config);
    }
    return _edgeDetectionInstance;
}

/**
 * Dispose singleton instance
 */
export function disposeAdvancedEdgeDetection(): void {
    if (_edgeDetectionInstance) {
        _edgeDetectionInstance.dispose();
        _edgeDetectionInstance = null;
    }
}
